"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuthWithLocationSync } from "@/hooks/use-auth-with-location-sync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { trackAction } from "@/lib/track-action";

type LoginFormProps = {
  redirectTo?: string;
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
  onLoginFailed?: (email: string, password: string) => void;
  extraContent?: React.ReactNode;
};

export default function LoginForm({ redirectTo = "/", onSuccess, onSwitchToRegister, onLoginFailed, extraContent }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isLoading } = useAuthWithLocationSync();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const result = await login(email, password);

    if (result.ok) {
      trackAction("SUCCESSFUL-LOGIN");
      if (onSuccess) {
        onSuccess();
      } else {
        router.push(redirectTo);
      }
    } else {
      trackAction("UNSUCCESSFUL-LOGIN");
      setError(result.error || "Error al iniciar sesión");
      if (onLoginFailed && result.status === 404) onLoginFailed(email, password);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email o celular</Label>
        <Input
          id="email"
          placeholder="Email o celular"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-4">
        ¿No tienes cuenta?{" "}
        {onSwitchToRegister ? (
          <button
            type="button"
            onClick={() => { trackAction("GO-TO-REGISTRATION"); onSwitchToRegister(); }}
            className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            Regístrate
          </button>
        ) : (
          <Link
            href={redirectTo !== "/" ? `/registro?redirect=${encodeURIComponent(redirectTo)}` : "/registro"}
            className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            Regístrate
          </Link>
        )}
      </p>

      {extraContent}

      <Button
        type="submit"
        className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
        size="lg"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Iniciando sesión...
          </>
        ) : (
          "Iniciar sesión"
        )}
      </Button>
    </form>
  );
}
